import React, { memo, useEffect, useMemo, useState } from "react";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { WidgetType } from "@intelligems/ig-types";
import { Box, Grid } from "@mui/material";
import { CustomTabs } from "@src/components/Tabs/CustomTabs";
import { IgAlert } from "@src/components/Utils/IgAlert";
import { MessageBoxOverview } from "@src/domains/WidgetLibrary/MessageBox/MessageBoxOverview";
import { OfferProgressBarOverview } from "@src/domains/WidgetLibrary/OfferProgressBar/OfferProgressBarOverview";
import { QuantityButtonsOverview } from "@src/domains/WidgetLibrary/QuantityButtons/QuantityButtonsOverview";
import { ShippingProgressBarOverview } from "@src/domains/WidgetLibrary/ShippingProgressBar/ShippingProgressBarOverview";
import { useDispatch, useSelector } from "@src/store";
import { getTabIndex } from "@src/store/slices/components/tab";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import {
  closeWidget,
  getWidgetMode,
  selectedWidget,
} from "@src/store/slices/widgets";
import {
  offerProgressBarSetup,
  quantityButtonSetup,
  shippingProgressBarSetup,
} from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

export const WidgetLibraryBody = memo(() => {
  const dispatch = useDispatch();
  const widget = useSelector(selectedWidget);
  const widgetMode = useSelector(getWidgetMode);
  const activeTabIndex = useSelector(getTabIndex(WidgetLibraryBody.name)) ?? 0;

  const isQuantityButtonsDialogOpen = useSelector(
    isDialogOpen("quantityButtonsFullPageEditor")
  );
  const isMessageBoxDialogOpen = useSelector(
    isDialogOpen("messageBoxFullPageEditor")
  );
  const isOfferProgressBarDialogOpen = useSelector(
    isDialogOpen("offerProgressBarFullPageEditor")
  );
  const isShippingProgressBarDialogOpen = useSelector(
    isDialogOpen("shippingProgressBarFullPageEditor")
  );

  const isAnyDialogOpen =
    isQuantityButtonsDialogOpen ||
    isMessageBoxDialogOpen ||
    isOfferProgressBarDialogOpen ||
    isShippingProgressBarDialogOpen;

  const [dialogWasOpened, setDialogWasOpened] = useState(false);

  useEffect(() => {
    if (widget && (widgetMode === "new" || widgetMode === "edit")) {
      openWidgetDialog(widget.widgetType, widget.id, dispatch);
    }
  }, [widget, widgetMode, dispatch]);

  useEffect(() => {
    if (isAnyDialogOpen) {
      setDialogWasOpened(true);
    }
  }, [isAnyDialogOpen]);

  useEffect(() => {
    if (!isAnyDialogOpen && widget && dialogWasOpened) {
      dispatch(closeWidget(undefined));
      setDialogWasOpened(false);
    }
  }, [isAnyDialogOpen, widget, dialogWasOpened, dispatch]);

  const bannerMessage = useMemo(() => {
    if (activeTabIndex === 3) return null;
    const componentNames = {
      0: "quantity buttons",
      1: "an offer progress bar",
      2: "a shipping progress bar",
    };
    const componentName = componentNames[activeTabIndex];
    const endText =
      activeTabIndex === 1 ? "Already added?" : "Already installed?";
    return `To add ${componentName} to an Offer, follow these instructions. ${endText} You're all set 😎`;
  }, [activeTabIndex]);

  const docLink = useMemo(() => {
    const documentationLinks = {
      0: quantityButtonSetup,
      1: offerProgressBarSetup,
      2: shippingProgressBarSetup,
    };
    return documentationLinks[activeTabIndex] || quantityButtonSetup;
  }, [activeTabIndex]);

  return (
    <>
      <IgTypography variant={"h1"} sx={{ fontWeight: 600 }}>
        Global Styles
      </IgTypography>
      <Grid
        container
        display={"flex"}
        alignItems={"center"}
        marginTop={"15px"}
        rowSpacing={2}
      >
        <Grid item>
          <CustomTabs id={WidgetLibraryBody.name} aria-label={`Widget Library`}>
            <CustomTabs.IgTab label="Quantity Buttons" />
            <CustomTabs.IgTab label="Offer Progress Bars" />
            <CustomTabs.IgTab label="Shipping Progress Bars" />
            <CustomTabs.IgTab label="Offer messages" />
          </CustomTabs>
        </Grid>
      </Grid>
      {bannerMessage && (
        <IgAlert
          severity={"info"}
          title={"Installation Required:"}
          message={bannerMessage}
          buttons={[
            <IgButton
              onClick={() => openNewTab(docLink)}
              sx={{ marginRight: "8px" }}
              key={"doc-link"}
            >
              Installation steps
            </IgButton>,
          ]}
          sx={{ marginTop: "24px" }}
        />
      )}
      <Box marginY={"24px"}>
        <CustomTabs.Content id={WidgetLibraryBody.name}>
          <QuantityButtonsOverview />
          <OfferProgressBarOverview />
          <ShippingProgressBarOverview />
          <MessageBoxOverview />
        </CustomTabs.Content>
      </Box>
    </>
  );
});

const DIALOG_CONFIG = {
  messageBoxPopup: "messageBoxFullPageEditor",
  messageBoxSlideOut: "messageBoxFullPageEditor",
  discountProgressBar: "offerProgressBarFullPageEditor",
  shippingProgressBar: "shippingProgressBarFullPageEditor",
  quantityButtons: "quantityButtonsFullPageEditor",
} as const;

const openWidgetDialog = (
  type: WidgetType,
  widgetId: string,
  dispatch: ReturnType<typeof useDispatch>
) => {
  const dialogName = DIALOG_CONFIG[type];
  if (dialogName) {
    dispatch(
      setDialog({
        dialog: dialogName,
        open: true,
        props: {
          widgetId,
          variationId: "",
          widgetType: type,
        },
      })
    );
  }
};
